/**
 * WorkerListToolbar — the ONE filter row for every worker-portal list.
 *
 * Owner audit 2026-09-04: my request 的 attendance 和 process 的状态和 search 的 UI 不一致 …
 * 整个 worker potal 的，是否要有统一的状态 filter 和日期之类的 filter，统一的位置和 ui？
 *
 * A census of the eight list surfaces found SIX status vocabularies and THREE positions:
 *
 *   | surface             | options                              | "All" | search | date |
 *   |---------------------|--------------------------------------|-------|--------|------|
 *   | process requests    | All / Pending / Completed / Closed   | first | yes    | no   |
 *   | attendance requests | All / Pending / Approved / Rejected   | first | no     | no   |
 *   | my tasks            | Pending / Completed / All             | LAST  | no     | no   |
 *   | process approvals   | Pending / Reviewed                   | –     | yes    | no   |
 *   | attendance approvals| Pending / Reviewed                   | –     | no     | no   |
 *   | face reviews        | none                                 | –     | no     | no   |
 *   | time entries        | Today / Week / Month / Custom (dates)| –     | no     | yes  |
 *   | leave               | none                                 | –     | no     | no   |
 *
 * Position was worse than the vocabulary: My Tasks put its control in the TITLE row, attendance
 * requests put `+ New request` alone on one row and the filter on the next, and process requests
 * crowded filter + search + button onto one. Three answers to "where do I change what I am
 * looking at".
 *
 * This component owns POSITION and LOOK. Each page supplies its options, and the vocabulary is
 * chosen by the page's ROLE rather than by its module — see `STATUS_SETS` below. That is the
 * part that stops the drift: a module-shaped answer is what produced Completed/Closed on one
 * tab and Approved/Rejected on the other for the same question.
 *
 * NOT given a date range by default. Only lists that grow without bound get one; Leave and
 * Tasks are bounded by construction and a control that filters twelve rows is decoration
 * (svdy-rules §16).
 */
import React from 'react';
import {
  Box, Stack, TextField, ToggleButton, ToggleButtonGroup, InputAdornment, Typography,
} from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * The vocabulary, keyed by what the list IS rather than which module it came from.
 *
 * `submitted`  — things I sent in. A request is approved or rejected; it is not "closed".
 * `assigned`   — things I must do. Completed, not approved: I finish a form, nobody judges it.
 * `decided`    — things awaiting my decision. Two SCOPES, not filters: Reviewed is a different
 *                query, because a request I decided has advanced past me and is not in the
 *                pending response to filter.
 *
 * "All" is FIRST wherever it exists. My Tasks had it last, which is the same control reading in
 * a different order on two pages one click apart.
 */
/**
 * Which of the four outcomes a status belongs to. MOBILE TWIN:
 * `svdy-mobile/src/processStatus.ts::requestStatusBucket` — the sets are asserted equal by
 * `src/components/worker/submittedOutcomes.test.ts`.
 *
 * The three DECIDED groups are enumerated and `in_progress` is the ELSE branch, on purpose: a status in no
 * group is invisible on every chip except All, which is how `withdrawn` disappeared from three surfaces.
 * An unrecognised state now reads as "still moving" — visible, and the likeliest truth for one this client
 * has not been taught (session-discipline §12, fail in the safe direction).
 */
const SUBMITTED_COMPLETED = ['completed', 'approved'];
const SUBMITTED_REJECTED = ['rejected', 'failed'];
/** Ended without a ruling — `UNDECIDED_INSTANCE_STATUSES` server-side. A withdrawal is not a rejection. */
const SUBMITTED_WITHDRAWN = ['withdrawn', 'cancelled'];

export function submittedOutcome(status: string | null | undefined): 'in_progress' | 'completed' | 'rejected' | 'withdrawn' {
  const s = (status ?? '').toLowerCase();
  if (SUBMITTED_COMPLETED.indexOf(s) !== -1) return 'completed';
  if (SUBMITTED_REJECTED.indexOf(s) !== -1) return 'rejected';
  if (SUBMITTED_WITHDRAWN.indexOf(s) !== -1) return 'withdrawn';
  return 'in_progress';
}

/** `true` when a row belongs under `filter`. `all` matches everything. */
export function matchesSubmittedFilter(status: string | null | undefined, filter: string): boolean {
  if (filter === 'all') return true;
  return submittedOutcome(status) === filter;
}

export const SUBMITTED_OUTCOME_SETS = {
  completed: SUBMITTED_COMPLETED,
  rejected: SUBMITTED_REJECTED,
  withdrawn: SUBMITTED_WITHDRAWN,
};

export const STATUS_SETS = {
  // FOUR OUTCOMES since 2026-09-10, in the order the phone shows them (owner: *"worker web portal 中的
  // my request 的 filter 跟手机端的不一样！！要一致"*).
  //
  // It was `['all', 'pending', 'approved', 'rejected']`, and `rejected` held
  // `rejected / failed / cancelled / withdrawn` — so a WITHDRAWAL was filed under Rejected, which is the
  // exact defect fixed on the phone the same day and in `get_process_summary` before that. Three surfaces,
  // one wrong grouping. A submitted thing has four fates: still moving, approved, refused, or ended with
  // nobody ruling on it.
  //
  // `All` FIRST, and the PHONE was moved to match rather than this. `listFilterContract.test.ts` already
  // required All-first for every set and `assigned` follows it, so changing this one would have broken a
  // convention in three places to accommodate one — and that assertion records no reason to weigh against,
  // it is a bare ordering rule (session-discipline §19). Same four words, same order, both surfaces
  // (`svdy-mobile/src/processStatus.ts`).
  submitted: ['all', 'in_progress', 'completed', 'rejected', 'withdrawn'],
  assigned: ['all', 'pending', 'completed'],
  decided: ['pending', 'reviewed'],
} as const;

export type StatusSetName = keyof typeof STATUS_SETS;

interface Props {
  /** Which vocabulary this list uses. Decides the options AND their labels. */
  set: StatusSetName;
  value: string;
  onChange: (next: string) => void;
  /** Inline stats line, rendered on the left. Omit when the page has none. */
  stats?: React.ReactNode;
  /** Search box. Omitted entirely when `onSearchChange` is absent. */
  search?: string;
  onSearchChange?: (next: string) => void;
  /** The page's primary action, rendered last. */
  action?: React.ReactNode;
}

const WorkerListToolbar: React.FC<Props> = ({
  set, value, onChange, stats, search, onSearchChange, action,
}) => {
  const { t } = useTranslation();
  const options = STATUS_SETS[set];

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        mb: 1.5,
      }}
    >
      {/* Left: what this list contains. An empty Box keeps the controls on the right even when
          the page has no stats, so the toolbar sits in the same place on every surface. */}
      <Box sx={{ minWidth: 0 }}>
        {stats ?? <Typography variant="body2" color="text.secondary">&nbsp;</Typography>}
      </Box>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          value={value}
          exclusive
          onChange={(_, v) => v && onChange(v)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.5, borderColor: 'divider',
            },
          }}
        >
          {options.map((o) => (
            <ToggleButton key={o} value={o}>{t(`worker:listFilter.${o}`)}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        {onSearchChange && (
          <TextField
            size="small"
            placeholder={t('worker:listFilter.searchPlaceholder')}
            value={search ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ width: 200, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        {action}
      </Stack>
    </Box>
  );
};

export default WorkerListToolbar;
